import React, {useEffect, useState} from 'react';
import {useParams} from "react-router-dom";
import {getSingleCampaign, updateSingleCampaign} from "../../services/facebook-campaign-data.service";
import {searchDesignInfoWithSearchCriteria} from "../../../shopify/services/design-info-data.service";
import ShowEmptyListMessage from "../../../design/components/common/show-empty-list-message";
import ShowTabsList from "../../../common/ui/show-tabs-list";
import ShowLink from "../../../common/ui/show-link";
import {
    DEFAULT_DESIGN_CAMPAIGN_DASHBOARD_TAB,
    DESIGN_CAMPAIGN_DASHBOARD_TABS_LIST
} from "../../constants/campaign.constant";
import ParentProductDetail from "../../../pod/components/parent-product/edit-parent-product/parent-product-detail";
import {getAllTags} from "../../../common/services/tag-data-service";
import {getSingleParentProductById} from "../../../pod/services/parent-product-data-service";
import CampaignDetail from "./campaign-detail";
import CampaignDesigns from "./campaign-designs";
import {prepareParentFacebookSearchInterestList} from "../../utils/facebook-interest-util";
import {SAVE_DATA_TIME_DELAY} from "../../constants/facebook-constant";
import AdSetList from "../ad-set/ad-set-list";
import AdSetsListWithAdCreatives from "../ad-set/ad-set-list-with-ad-creatives";

const tabsList = DESIGN_CAMPAIGN_DASHBOARD_TABS_LIST;
const DesignCampaign = () => {
    const [selectedTab, setSelectedTab] = useState(DEFAULT_DESIGN_CAMPAIGN_DASHBOARD_TAB);
    const {campaignId} = useParams();
    const [campaign, setCampaign] = useState(null);
    const [designInfoList, setDesignInfoList] = useState([]);
    const [parentProduct, setParentProduct] = useState(null);
    const [allTags, setAllTags] = useState([]);
    const [parentProductSearchInterestList, setParentProductSearchInterestList] = useState([]);
    const [campaignInterests, setCampaignInterests] = useState({} as any);
    let [changesCount, setChangesCount] = useState(0);
    const [isCampaignDetailChanged, setIsCampaignDetailChanged] = useState(false);
    const [dataFetchInProgress, setDataFetchInProgress] = useState(false);

    useEffect(() => {
        getCampaignDetail();
        getTagsList();
    }, [campaignId]);

    const getCampaignDetail = async (fetchDesignAndParentProduct = true) => {
        const campaignResult = await getSingleCampaign(campaignId);
        setCampaign(campaignResult);
        setCampaignInterests(campaignResult.interests);

        if (campaignResult.parentProductId && fetchDesignAndParentProduct) {
            await getDesignInfoListByParentProductId(campaignResult.parentProductId);
            await getParentProductDetail(campaignResult.parentProductId);
        }
    }

    const getTagsList = async () => {
        const tagsListResult = await getAllTags();
        setAllTags(tagsListResult);
    }

    const getDesignInfoListByParentProductId = async (parentProductId) => {
        const designInfoListResult = await searchDesignInfoWithSearchCriteria([{
            field: 'parentId',
            condition: '==',
            value: parentProductId
        }]);

        setDesignInfoList(designInfoListResult);
    }

    const getParentProductDetail = async (parentProductId) => {
        const parentProductResult = await getSingleParentProductById(parentProductId);
        setParentProduct(parentProductResult);
        prepareParentFacebookSearchInterests(parentProductResult);
    }

    const prepareParentFacebookSearchInterests = (parentProduct) => {
        let searchInterestTextList = prepareParentFacebookSearchInterestList(parentProduct, allTags);
        setParentProductSearchInterestList(searchInterestTextList);
    }

    useEffect(() => {
        setIsCampaignDetailChanged(true);
        incrementChangesCount();
    }, [campaignInterests]);

    const saveCampaign = async () => {
        setDataFetchInProgress(true);

        const existingCampaign = {...campaign};
        existingCampaign.interests = {...campaignInterests};
        await updateSingleCampaign(existingCampaign);

        setIsCampaignDetailChanged(false);
        setDataFetchInProgress(false);
    }

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {

                if (changesCount > 0) {
                    resetChangesCount();
                    await saveCampaign();
                }

            }, SAVE_DATA_TIME_DELAY);

        return () => clearTimeout(timeoutId);
    }, [changesCount]);

    const incrementChangesCount = () => {
        setChangesCount(++changesCount);
    }

    const resetChangesCount = () => {
        setChangesCount(0);
    }

    return (
        <>

            <div className="page-container">

                <div className="items-center justify-start p-4 w-full">
                    <div className="flex justify-start items-center m-2 pb-2">
                        <div className={'ml-2'}>
                            <ShowLink label="Back"
                                      url="/facebook"/>
                        </div>

                        <div className="text-xl ml-4">
                            Campaign
                        </div>

                        <span className={'mx-2'}>|</span>
                        <div className="text-xl">
                            {campaign?.name}
                        </div>

                    </div>


                    <div className="justify-start mt-4">

                        <div className={'flex ml-4'}>
                            <ShowTabsList
                                setSelectedTab={setSelectedTab}
                                selectedTab={selectedTab}
                                tabsList={tabsList}/>
                        </div>

                        <div className="px-4 mt-4">

                            {selectedTab?.key === 'campaign' && campaign &&
                                <div>
                                    <div>
                                        <CampaignDetail
                                            isTable={false}
                                            afterEditAdCampaignAction={() => getCampaignDetail(false)}
                                            campaign={campaign}/>
                                    </div>

                                    <div className={'mt-4'}>
                                        <AdSetsListWithAdCreatives campaign={campaign}/>
                                    </div>
                                </div>
                            }


                            {selectedTab?.key === 'designs' &&
                                <div>
                                    <CampaignDesigns
                                        campaign={campaign}
                                        campaignInterests={campaignInterests}
                                        setCampaignInterests={setCampaignInterests}
                                        parentProduct={parentProduct}
                                        parentProductSearchInterestList={parentProductSearchInterestList}
                                        designInfoList={designInfoList}/>
                                </div>
                            }

                            {selectedTab?.key === 'parent-product' && parentProduct &&
                                <div>
                                    <ShowEmptyListMessage
                                        dataList={parentProduct ? [parentProduct] : []}
                                        dataFetchInProgress={false}
                                        label={'Parent Product'}/>

                                    {parentProduct &&
                                        <ParentProductDetail
                                            parentProduct={parentProduct}
                                            setParentProduct={setParentProduct}
                                            allTags={allTags}/>
                                    }
                                </div>
                            }

                        </div>

                    </div>

                </div>
            </div>

        </>
    )
}

export default DesignCampaign
